import { useMemo, useState } from 'react';
import { CategoryPhTree } from '../../../../../../../../../../interfaces';
import { AnimationDirection, MenuView, UseMenuSelectionProps } from './interfaces';

/**
 * Keeps the mobile category hierarchy and navigation direction in local state.
 *
 * ---
 * 🧠 **Context**
 *
 * - Selection survives closing because CategoriesMobile stays mounted.
 * - Selecting a subcategory invokes the route action before onClose.
 *
 * ---
 * @example
 * ```ts
 * const menu = useMenuSelection({
 *   onClose: () => console.info('categories-closed'),
 *   onSubcategorySelect: (department, category, subcategory) => console.info(department, category, subcategory),
 * });
 * const selectMedicines = () => menu.handleSelectDepartment({ id: 'medicamentos', name: 'Medicamentos' });
 * ```
 *
 * ---
 * @function useMenuSelection
 * @param {UseMenuSelectionProps} props - Subcategory commit and close callbacks.
 * @returns {ReturnType<typeof useMenuSelection>} Selected nodes, current level, animation direction and selection handlers.
 */
export function useMenuSelection({ onSubcategorySelect, onClose }: UseMenuSelectionProps) {
	const [selectedDepartment, setSelectedDepartment] = useState<CategoryPhTree>();
	const [selectedCategory, setSelectedCategory] = useState<CategoryPhTree>();
	const [direction, setDirection] = useState<AnimationDirection>('forward');

	const selectedItemName = useMemo(
		() => selectedCategory?.name || selectedDepartment?.name || 'Departamentos',
		[selectedDepartment, selectedCategory],
	);

	const shouldShowBackButton = !!selectedDepartment || !!selectedCategory;

	const currentView = useMemo<MenuView>(() => {
		if (selectedCategory) return 'subcategories';

		if (selectedDepartment) return 'categories';

		return 'departments';
	}, [selectedDepartment, selectedCategory]);

	const handleBack = () => {
		setDirection('backward');

		if (!!selectedCategory?.id) {
			setSelectedCategory(undefined);

			return;
		}

		setSelectedDepartment(undefined);
	};

	const handleSelectDepartment = (department: CategoryPhTree) => {
		setDirection('forward');
		setSelectedDepartment(department);
	};

	const handleSelectCategory = (category: CategoryPhTree) => {
		setDirection('forward');
		setSelectedCategory(category);
	};

	const handleSelectSubcategory = (subcategoryId: string) => {
		onSubcategorySelect(selectedDepartment?.id || '', selectedCategory?.id || '', subcategoryId);
		onClose();
	};

	return {
		selectedDepartment,
		selectedCategory,
		selectedItemName,
		shouldShowBackButton,
		currentView,
		direction,
		handleBack,
		handleSelectDepartment,
		handleSelectCategory,
		handleSelectSubcategory,
	};
}
